import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store, Globe, Shield, CheckCircle, Plus, ExternalLink, RefreshCw, Settings, Trash2, AlertTriangle } from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function StorefrontBuilderPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [storefronts, setStorefronts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDomain, setShowDomain] = useState(null);
  const [newStorefront, setNewStorefront] = useState({ name: '', subdomain: '', template: 'modern' });
  const [newDomain, setNewDomain] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const profile = await authService.getProfile();
      setUser(profile.member);
      const res = await apiClient.get('/api/v1/storefronts');
      setStorefronts(res.data.storefronts || res.data || []);
    } catch (err) {
      console.error('Failed to load:', err);
      setStorefronts([
        { id: 'sf_demo', name: 'My Store', subdomain: 'mystore', status: 'active', domains: [{ domain: 'mystore.commerceindex.com', verified: true, ssl: true, primary: true }], template: 'modern', created_at: new Date().toISOString(), go_live_checklist: { products: true, payment: true, shipping: false, legal: true, domain: true } },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const createStorefront = async () => {
    try {
      const res = await apiClient.post('/api/v1/storefronts', newStorefront);
      setStorefronts([...storefronts, res.data]);
      setShowCreate(false);
      setNewStorefront({ name: '', subdomain: '', template: 'modern' });
      toast.success('Storefront created!');
    } catch (err) {
      toast.error('Failed to create storefront', { description: err.response?.data?.detail || 'Try again' });
    }
  };

  const addDomain = async (sfId) => {
    try {
      await apiClient.post(`/api/v1/storefronts/${sfId}/domains`, { domain: newDomain });
      toast.success('Domain added! Verify DNS records.');
      setNewDomain('');
      setShowDomain(null);
      loadData();
    } catch (err) {
      toast.error('Failed to add domain');
    }
  };

  const goLive = async (sfId) => {
    try {
      const res = await apiClient.post(`/api/v1/storefronts/${sfId}/go-live`);
      if (res.data.success) {
        toast.success('Storefront is LIVE!');
        loadData();
      } else {
        toast.error('Go-live validation failed', { description: res.data.issues?.join(', ') });
      }
    } catch (err) {
      toast.error('Go-live check failed');
    }
  };

  const templates = [
    { id: 'modern', name: 'Modern', desc: 'Clean, minimal design' },
    { id: 'boutique', name: 'Boutique', desc: 'Elegant, fashion-forward' },
    { id: 'marketplace', name: 'Marketplace', desc: 'Multi-vendor layout' },
    { id: 'minimal', name: 'Minimal', desc: 'Content-focused, fast' },
  ];

  const ChecklistItem = ({ label, done }) => (
    <div className="flex items-center gap-2 text-sm">
      {done ? <CheckCircle className="w-4 h-4 text-[#3EF1C7]" /> : <AlertTriangle className="w-4 h-4 text-[#FFC857]" />}
      <span className={done ? 'text-[#3EF1C7]' : 'text-[#FFC857]'}>{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Storefront Builder</h1>
            <p className="text-sm text-[#9CB0C7]">Storefront Builder</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-[#7A5CFF] text-white rounded-lg hover:bg-[#6B4FE0] transition">
              <Plus className="w-4 h-4" /> New Storefront
            </button>
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">
              Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Storefronts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {storefronts.map(sf => (
            <div key={sf.id} className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#7A5CFF]/20 rounded-lg flex items-center justify-center">
                    <Store className="w-5 h-5 text-[#7A5CFF]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{sf.name}</h3>
                    <p className="text-xs text-[#9CB0C7]">{sf.subdomain}.commerceindex.com</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${sf.status === 'active' ? 'bg-[#3EF1C7]/10 text-[#3EF1C7]' : 'bg-[#FFC857]/10 text-[#FFC857]'}`}>
                  {sf.status === 'active' ? 'LIVE' : 'DRAFT'}
                </span>
              </div>

              {/* Domains */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#9CB0C7] font-medium">Domains</span>
                  <button onClick={() => setShowDomain(sf.id)} className="text-xs text-[#7A5CFF] hover:underline">+ Add Domain</button>
                </div>
                {(sf.domains || []).map((d, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#0B1120] rounded-lg px-3 py-2 mb-1">
                    <div className="flex items-center gap-2">
                      <Globe className="w-3 h-3 text-[#9CB0C7]" />
                      <span className="text-sm text-white">{d.domain}</span>
                      {d.primary && <span className="text-[10px] bg-[#7A5CFF]/20 text-[#7A5CFF] px-1.5 rounded">PRIMARY</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {d.ssl && <Shield className="w-3 h-3 text-[#3EF1C7]" />}
                      {d.verified ? <CheckCircle className="w-3 h-3 text-[#3EF1C7]" /> : <RefreshCw className="w-3 h-3 text-[#FFC857] animate-spin" />}
                    </div>
                  </div>
                ))}
                {showDomain === sf.id && (
                  <div className="flex gap-2 mt-2">
                    <input value={newDomain} onChange={e => setNewDomain(e.target.value)} placeholder="yourdomain.com" className="flex-1 bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-white text-sm" />
                    <button onClick={() => addDomain(sf.id)} className="px-3 py-2 bg-[#7A5CFF] text-white rounded-lg text-sm">Add</button>
                  </div>
                )}
              </div>

              {/* Go-Live Checklist */}
              <div className="mb-4">
                <span className="text-sm text-[#9CB0C7] font-medium block mb-2">Go-Live Checklist</span>
                <div className="grid grid-cols-2 gap-1">
                  <ChecklistItem label="Products added" done={sf.go_live_checklist?.products} />
                  <ChecklistItem label="Payment configured" done={sf.go_live_checklist?.payment} />
                  <ChecklistItem label="Shipping rates set" done={sf.go_live_checklist?.shipping} />
                  <ChecklistItem label="Legal pages added" done={sf.go_live_checklist?.legal} />
                  <ChecklistItem label="Domain verified" done={sf.go_live_checklist?.domain} />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button onClick={() => goLive(sf.id)} className="flex-1 py-2 bg-[#3EF1C7]/10 text-[#3EF1C7] rounded-lg text-sm font-medium hover:bg-[#3EF1C7]/20 transition">
                  Go Live
                </button>
                <button className="px-3 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">
                  <Settings className="w-4 h-4" />
                </button>
                <button className="px-3 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-[#F55454] transition">
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
            <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-white mb-4">Create New Storefront</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-[#9CB0C7] block mb-1">Store Name</label>
                  <input value={newStorefront.name} onChange={e => setNewStorefront({ ...newStorefront, name: e.target.value })} className="w-full bg-[#0B1120] border border-[#22304A] rounded-lg px-3 py-2 text-white" placeholder="My Awesome Store" />
                </div>
                <div>
                  <label className="text-sm text-[#9CB0C7] block mb-1">Subdomain</label>
                  <div className="flex items-center">
                    <input value={newStorefront.subdomain} onChange={e => setNewStorefront({ ...newStorefront, subdomain: e.target.value })} className="flex-1 bg-[#0B1120] border border-[#22304A] rounded-l-lg px-3 py-2 text-white" placeholder="mystore" />
                    <span className="bg-[#22304A] px-3 py-2 text-[#9CB0C7] text-sm rounded-r-lg">.commerceindex.com</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-[#9CB0C7] block mb-2">Template</label>
                  <div className="grid grid-cols-2 gap-2">
                    {templates.map(t => (
                      <button key={t.id} onClick={() => setNewStorefront({ ...newStorefront, template: t.id })} className={`p-3 rounded-lg border text-left transition ${newStorefront.template === t.id ? 'border-[#7A5CFF] bg-[#7A5CFF]/10' : 'border-[#22304A] hover:border-[#7A5CFF]/50'}`}>
                        <span className="text-white text-sm font-medium block">{t.name}</span>
                        <span className="text-[#9CB0C7] text-xs">{t.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={createStorefront} className="flex-1 py-2 bg-[#7A5CFF] text-white rounded-lg font-medium hover:bg-[#6B4FE0] transition">Create Storefront</button>
                  <button onClick={() => setShowCreate(false)} className="px-4 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
